<template>
  <div ref="wrapper"
    class="presence-stack-wrapper">
    <div class="presence-avatar-stack"
      @click.stop="toggleDropdown">
      <div
        v-for="member in visibleMembers"
        :key="member.connectionId"
        class="presence-avatar"
        :class="{ 'presence-avatar--self': member.isSelf }"
        :style="{ backgroundColor: member.color }"
        :title="member.activeTab ? `${member.fullName} (${member.isEditing ? 'editing' : 'viewing'} ${member.activeTab})` : member.fullName"
      >
        {{ member.initials }}
        <span v-if="member.isEditing"
          class="presence-editing-dot"
          aria-label="editing" />
      </div>
      <div v-if="overflowCount > 0"
        class="presence-avatar presence-avatar--overflow">
        +{{ overflowCount }}
      </div>
    </div>

    <div v-if="isDropdownOpen"
      class="presence-dropdown">
      <div
        v-for="member in sortedMembers"
        :key="member.connectionId"
        class="presence-dropdown-item"
      >
        <div
          class="presence-dropdown-avatar"
          :class="{ 'presence-avatar--self': member.isSelf }"
          :style="{ backgroundColor: member.color }"
        >
          {{ member.initials }}
          <span v-if="member.isEditing"
            class="presence-editing-dot"
            aria-label="editing" />
        </div>
        <div class="presence-dropdown-info">
          <span class="presence-dropdown-name">
            {{ member.fullName }}{{ member.isSelf ? ' (you)' : '' }}
          </span>
          <span class="presence-dropdown-status">
            <template v-if="member.activeTab">
              {{ member.isEditing ? 'Editing' : 'Viewing' }} {{ member.activeTab }}
            </template>
            <template v-else>
              {{ member.isEditing ? 'Editing' : 'Viewing' }}
            </template>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { presenceProvider } from '@/services/presence'
import { getInitials, getFullName } from '@/utils/PersonNameFunctions'

const MAX_VISIBLE = 5

const COLORS = [
  '#E91E63', '#9C27B0', '#3F51B5', '#0288D1',
  '#00897B', '#388E3C', '#F57C00', '#5D4037',
  '#546E7A', '#C62828'
]

function getColor(userId) {
  let hash = 0
  for (const c of String(userId)) hash = (hash * 31 + c.charCodeAt(0)) >>> 0
  return COLORS[hash % COLORS.length]
}

export default {
  name: 'PresenceAvatarStack',

  props: {
    quoteId: { type: String, required: true },
    currentUser: { type: Object, required: true },
    isEditing: { type: Boolean, default: false },
    activeTab: { type: String, default: '' }
  },

  data() {
    return {
      members: [],
      session: null,
      sessionId: null,
      unsubscribePresence: null,
      isDropdownOpen: false
    }
  },

  computed: {
    enrichedMembers() {
      return this.members.filter(m => m.userId).map(m => ({
        connectionId: m.sessionId,
        initials:     getInitials(m),
        fullName:     getFullName(m),
        color:        getColor(m.userId || m.sessionId),
        isSelf:       m.userId === this.currentUser.userId && m.sessionId === this.sessionId,
        isEditing:    !!m.isEditing,
        activeTab:    m.activeTab || ''
      }))
    },

    // Self first, then others
    sortedMembers() {
      return [...this.enrichedMembers].sort((a, b) => (b.isSelf ? 1 : 0) - (a.isSelf ? 1 : 0))
    },

    visibleMembers() {
      return this.sortedMembers.slice(0, MAX_VISIBLE)
    },

    overflowCount() {
      return Math.max(0, this.sortedMembers.length - MAX_VISIBLE)
    }
  },

  watch: {
    isEditing(newVal) {
      if (!this.session) return
      this.session.update({ isEditing: newVal })
        .catch(e => console.warn('[PresenceAvatarStack] update error:', e))
    },
    activeTab(newVal) {
      if (!this.session) return
      this.session.update({ activeTab: newVal })
        .catch(e => console.warn('[PresenceAvatarStack] update error:', e))
    },
    // The quote view is reused when switching between a main quote and its
    // additional quotes (same route component, different quoteId) — re-key the
    // presence session or it stays attached to the previous quote.
    quoteId(newId, oldId) {
      if (!newId || newId === oldId) return
      this.reconnect()
    }
  },

  async mounted() {
    this._onDocumentClick = (e) => {
      if (this.$refs.wrapper && !this.$refs.wrapper.contains(e.target)) {
        this.isDropdownOpen = false
      }
    }
    document.addEventListener('click', this._onDocumentClick)
    this._connectToken = 0

    await this.connect()
  },

  async beforeDestroy() {
    document.removeEventListener('click', this._onDocumentClick)
    this._connectToken++ // invalidate any in-flight connect
    await this.disconnect()
  },

  methods: {
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen
    },

    async connect() {
      if (!process.env.VUE_APP_FIREBASE_DATABASE_URL) return

      const token = ++this._connectToken
      try {
        const session = await presenceProvider.enter(this.quoteId, this.currentUser.companyId, {
          userId:      this.currentUser.userId,
          firstName:   this.currentUser.firstName,
          lastName:    this.currentUser.lastName,
          displayName: this.currentUser.displayName,
          isEditing:   this.isEditing,
          activeTab:   this.activeTab
        })

        if (!session) return   // 403 — presence disabled for this company

        // A newer connect/disconnect superseded this one while enter() was in flight.
        if (token !== this._connectToken) {
          session.leave().catch(() => {})
          return
        }

        this.session = session
        this.sessionId = session.sessionId

        this.unsubscribePresence = presenceProvider.subscribeToMembers(this.quoteId, (members) => {
          this.members = members
        })

        this.$emit('ready')
      } catch (e) {
        console.warn('[PresenceAvatarStack] Firebase error:', e)
      }
    },

    async disconnect() {
      if (this.unsubscribePresence) {
        this.unsubscribePresence()
        this.unsubscribePresence = null
      }
      if (this.session) {
        const session = this.session
        this.session = null
        this.sessionId = null
        try { await session.leave() } catch (e) { /* ignore */ }
      }
      this.members = []
    },

    async reconnect() {
      await this.disconnect()
      await this.connect()
    }
  }
}
</script>

<style scoped>
.presence-stack-wrapper {
  position: relative;
  display: inline-flex;
}

.presence-avatar-stack {
  display: flex;
  align-items: center;
  flex-direction: row-reverse; /* rightmost avatar is first in DOM, creates natural overlap */
  cursor: pointer;
}

.presence-avatar-stack > * {
  margin-left: -8px;
}

.presence-avatar-stack > *:last-child {
  margin-left: 0;
}

.presence-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  /* Theme-aware: the ring separating overlapping avatars must match the surface
     behind them. --background-color is set per theme on <html> (see themes/*). */
  border: 2px solid var(--background-color, #fff);
  user-select: none;
  position: relative;
  transition: transform 0.15s ease;
}

.presence-avatar:hover {
  transform: scale(1.15);
  z-index: 10;
}

.presence-avatar--self {
  box-shadow: 0 0 0 2px #3273dc;
}

.presence-avatar--overflow {
  background-color: #6b7280;
  font-size: 10px;
}

.presence-editing-dot {
  position: absolute;
  bottom: -1px;
  right: -1px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background-color: #F59E0B;
  border: 2px solid var(--background-color, #fff);
  animation: editing-pulse 1.4s ease-in-out infinite;
  pointer-events: none;
}

@keyframes editing-pulse {
  0%, 100% { opacity: 1;    transform: scale(1);   }
  50%       { opacity: 0.55; transform: scale(0.8); }
}

/* Dropdown */
.presence-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 100;
  min-width: 220px;
  max-height: 320px;
  overflow-y: auto;
  background: var(--background-color, #fff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: 4px 0;
}

/* Mobile — bottom sheet */
@media (max-width: 480px) {
  .presence-dropdown {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    top: auto;
    min-width: unset;
    width: 100%;
    max-height: 60vh;
    border-radius: 14px 14px 0 0;
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15);
    padding-bottom: env(safe-area-inset-bottom, 8px);
  }

  .presence-dropdown::before {
    content: '';
    display: block;
    width: 36px;
    height: 4px;
    background: var(--border-color, #d1d5db);
    border-radius: 2px;
    margin: 10px auto 6px;
  }

  .presence-dropdown-item {
    padding: 12px 16px;
  }

  .presence-dropdown-name {
    font-size: 14px;
  }

  .presence-dropdown-status {
    font-size: 12px;
  }

  .presence-avatar {
    width: 36px;
    height: 36px;
  }
}

.presence-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
}

.presence-dropdown-item:hover {
  background-color: var(--background-hover-color, #f9fafb);
}

.presence-dropdown-avatar {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  border: 2px solid var(--background-color, #fff);
  position: relative;
}

.presence-dropdown-avatar.presence-avatar--self {
  box-shadow: 0 0 0 2px #3273dc;
}

.presence-dropdown-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.presence-dropdown-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--bulma-text-strong, #111827);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.presence-dropdown-status {
  font-size: 11px;
  color: var(--bulma-text, #6b7280);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
